import { action, observable, computed } from 'mobx';
import {
    IVtoFoundationOverlayProps,
    IVtoFoundationOverlayNodeContent,
    IVtoFoundationOverlayNodeData
} from '~exported/vto-foundation-overlay/VtoFoundationOverlay';
import { VtoBaseViewController } from './VtoBaseViewController';
import { emit, events } from '@estee/elc-service-bus';

export interface IProductVtoWindow extends Window {
    YMK: {
        open(
            loadLiveCam: boolean,
            imageObject: Record<string, unknown> | null,
            detectSkin: boolean
        ): void;
    };
}

declare let window: IProductVtoWindow;
export class VtoFoundationOverlayViewController extends VtoBaseViewController {
    @observable public _content: Map<number, IVtoFoundationOverlayNodeContent>;
    @observable public overlayData = {} as IVtoFoundationOverlayNodeData;
    @observable private _isDisclaimerChecked = false;

    constructor() {
        super();
        this._content = new Map();
        this.setIsVtoDisclosed();
    }

    @action
    public loaded = async (props: IVtoFoundationOverlayProps) => {
        const { nid, layout, data = [] } = props.config;
        this.content = {
            nid,
            layout,
            data
        };

        const [overlayData] = data;
        this.overlayData = overlayData || this.overlayData;

        this.isLoading = true;

        if (this.overlayData.showShadeReminderScreen) {
            await this.setBestMatch();
        }

        this.isLoading = false;
    };

    @computed
    get data() {
        return {
            bestMatch: this.bestMatch,
            content: this._content,
            isVtoDisclosed: this.isVtoDisclosed,
            isBestMatch: this.isBestMatch,
            isLoading: this.isLoading,
            title: this.title,
            subtitle: this.subtitle,
            isDisclaimerChecked: this.isDisclaimerChecked
        };
    }

    @action
    public toggleDisclaimerCheck = () => {
        this._isDisclaimerChecked = !this._isDisclaimerChecked;
    };

    public getContent(nid: number) {
        return this._content.get(nid);
    }

    public set content(input: IVtoFoundationOverlayNodeContent) {
        const { nid } = input;
        this._content.set(nid, { ...input });
    }

    public openFoundationVto = () => {
        const loadLiveCam = true;
        const imageObject = null;
        const detectSkin = true;
        if (typeof window.YMK === 'object') {
            window.YMK.open(loadLiveCam, imageObject, detectSkin);
        }

        if (this.isBestMatch) {
            this.retakeVtoShadeReminder();
        }
    };

    public selectShade = () => {
        emit(events.PRODUCT_SHADE_SELECTED, { shadeName: this.bestMatch.name });
        emit(events.VTO_FOUNDATION_OVERLAY_TOGGLED, { isOverlayOpened: false });
        this.retainShadeReminderOverlay();
    };

    public closeOverlay = () => {
        if (this.isBestMatch) {
            this.closeShadeReminderOverlay();
        }

        emit(events.VTO_FOUNDATION_OVERLAY_TOGGLED, { isOverlayOpened: false });
    };

    @computed
    get isBestMatch() {
        return !!this.bestMatch;
    }

    @computed
    get title() {
        const { title, bestMatchTitle } = this.overlayData;

        return this.isBestMatch ? bestMatchTitle : title;
    }

    @computed
    get subtitle() {
        const { copy: subtitle, bestMatchSubtitle } = this.overlayData;

        return this.isBestMatch ? bestMatchSubtitle : subtitle;
    }

    @computed
    public get isDisclaimerChecked() {
        return this._isDisclaimerChecked;
    }
}
